import React from 'react';
import { CircleCheck, ExternalLink } from 'lucide-react';
import { PublicSection, Reveal } from '@/components/public';
import { POS_PLATFORMS } from '@/config/integrations';

export default function PosDetailCards({ selectedId }) {
  return (
    <PublicSection
      id="pos-details"
      testId="pos-detail-cards-section"
      variant="alt"
      eyebrow="Integration Details"
      title="Which POS is right for your restaurant?"
      description="Every integration includes 2-way menu sync, Canadian tax compatibility, and direct order injection — here's who each platform fits best."
      padTop="pt-16 md:pt-20"
      padBottom="pb-16 md:pb-20"
    >
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {POS_PLATFORMS.map((pos, i) => {
          const selected = selectedId === pos.id;
          return (
            <Reveal key={pos.id} delay={i * 0.05}>
              <div
                id={`pos-card-${pos.id}`}
                data-testid={`pos-detail-card-${pos.id}`}
                className={`h-full flex flex-col bg-background border p-6 md:p-7 scroll-mt-24 transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1 ${
                  selected ? 'border-primary shadow-[0_0_0_1px_hsl(var(--primary)),0_8px_30px_-10px_hsl(var(--primary)/0.4)]' : 'border-border'
                }`}
              >
                <div className="flex items-center justify-between gap-4 mb-5">
                  <span className="flex items-center h-9 w-28">
                    <img src={pos.logo} alt={pos.logoAlt} className="max-h-full max-w-full object-contain" loading="lazy" />
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold uppercase tracking-wide">
                    <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    Active
                  </span>
                </div>

                <h3 className="text-lg md:text-xl font-heading font-black tracking-tight text-foreground">
                  {pos.name}
                </h3>
                <p className="mt-0.5 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  {pos.targetVenue}
                </p>

                <p className="mt-3.5 text-sm text-muted-foreground leading-relaxed">
                  <span className="font-semibold text-foreground">Best for:</span> {pos.bestFor}
                </p>

                <ul className="mt-4 space-y-2.5 flex-1">
                  {pos.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2.5">
                      <CircleCheck className="w-4 h-4 mt-0.5 shrink-0 text-primary" strokeWidth={2.5} />
                      <span className="text-sm text-foreground/85 leading-snug">{h}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-6 pt-5 border-t border-border space-y-2.5">
                  <a
                    href={pos.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`pos-card-primary-cta-${pos.id}`}
                    className="flex items-center justify-center gap-2 w-full h-11 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary-hover transition-colors"
                  >
                    Learn More & Connect POS <ExternalLink className="w-4 h-4" />
                  </a>
                  <a
                    href="/#lead-form-anchor"
                    data-testid={`pos-card-secondary-cta-${pos.id}`}
                    className="flex items-center justify-center w-full h-11 rounded-full border border-border bg-card text-sm font-semibold text-foreground hover:border-primary hover:text-primary transition-colors"
                  >
                    Talk to Our Team
                  </a>
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </PublicSection>
  );
}
